import { Box, Chip, Stack, Typography } from '@mui/material';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import type { FlowRun } from '../../types/flow-execution.types';

dayjs.extend(relativeTime);
dayjs.extend(utc);

interface Props {
  run: FlowRun;
  selected: boolean;
  onSelect: (execId: string) => void;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)}h`;
}

function ExecutionCard({ run, selected, onSelect }: Props) {
  const startedAt = dayjs.utc(run.startedAt).local();
  const tokenLabel = Math.round(run.totalTokens * 1000) / 1000;
  return (
    <Box
      onClick={() => onSelect(run.execId)}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
        {run.status === 'error' ? (
          <Warning size={16} weight='fill' color='#d32f2f' />
        ) : (
          <CheckCircle size={16} weight='fill' color='#2e7d32' />
        )}
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {startedAt.fromNow()}
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          title={startedAt.format('YYYY-MM-DD HH:mm:ss')}
        >
          {startedAt.format('MMM D, HH:mm')}
        </Typography>
        {run.isRetentionSlimmed && (
          <Chip size='small' label='Retention' variant='outlined' />
        )}
      </Stack>
      <Stack direction='row' spacing={1.5} flexWrap='wrap' sx={{ rowGap: 0.5 }}>
        {run.userName && (
          <Typography variant='caption' color='text.secondary'>
            {run.userName}
          </Typography>
        )}
        <Typography variant='caption' color='text.secondary'>
          {run.nodeCount} node{run.nodeCount === 1 ? '' : 's'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {formatDuration(run.durationMs)}
        </Typography>
        {tokenLabel > 0 && (
          <Typography variant='caption' color='text.secondary'>
            {tokenLabel} tok
          </Typography>
        )}
        {run.flowVersionHistoryId && (
          <Typography variant='caption' color='text.secondary'>
            v {run.flowVersionHistoryId.slice(0, 8)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default ExecutionCard;
